import { CheckCircle2 } from "lucide-react";
import { Card, CardContent, CardHeader, CardTitle, CardDescription } from "../ui/card";
import { Checkbox } from "../ui/checkbox";
import { Input } from "../ui/input";
import { Label } from "../ui/label";
import { Progress } from "../ui/progress";

const CHECKLIST_ITEMS = [
  { key: "client_info_verified", label: "Client info verified", desc: "Email & phone correct" },
  { key: "agreement_attached", label: "Agreement attached", desc: "PDF stored" },
  { key: "project_dates_confirmed", label: "Dates confirmed", desc: "Start & delivery reviewed" },
  { key: "scope_reviewed", label: "Scope reviewed", desc: "Deliverables clear" },
  { key: "payment_confirmed", label: "Payment confirmed", desc: "Terms verified" },
  { key: "kickoff_link_configured", label: "Planning call link ready", desc: "Booking link set" },
];

export default function ActivationChecklistTab({
  checklist,
  setChecklist,
  accountsPayable,
  setAccountsPayable,
  apFromIntake,
}) {
  const ap = accountsPayable || {};
  const setAp = (field, value) =>
    setAccountsPayable?.({ ...ap, [field]: value });
  const completed = Object.values(checklist).filter(v => v).length;
  const progress = (completed / 6) * 100;

  return (
    <div className="space-y-3 sm:space-y-4 mt-3 sm:mt-4">
      <Card>
        <CardHeader className="pb-2 sm:pb-3 px-3 sm:px-6">
          <CardTitle className="text-sm sm:text-base">Activation Checklist</CardTitle>
          <CardDescription className="text-xs sm:text-sm">Complete all items before activating</CardDescription>
        </CardHeader>
        <CardContent className="px-3 sm:px-6 space-y-3 sm:space-y-4">
          <div>
            <Progress value={progress} className="h-2" />
            <p className="text-xs text-center text-muted-foreground mt-1">
              {completed} of 6 complete
            </p>
          </div>

          {/* Who we invoice. A hard requirement to activate: every payment
              notice routes here rather than to the primary contact, who is
              usually the developer or marketing lead. Pre-filled from intake
              when sales captured it; editable here because every project that
              predates the field would otherwise be stuck unactivatable. */}
          {setAccountsPayable && (
            <div className="space-y-2 pb-3 border-b">
              <div>
                <Label className="text-sm">
                  Accounts Payable Manager <span className="text-red-500">*</span>
                </Label>
                <p className="text-xs text-muted-foreground mt-0.5">
                  {apFromIntake
                    ? "Captured at intake. Every payment notice goes here."
                    : "Not captured at intake — required before this project can activate."}
                </p>
              </div>
              <div className="grid grid-cols-1 sm:grid-cols-2 gap-2">
                <Input
                  value={ap.name || ""}
                  onChange={(e) => setAp("name", e.target.value)}
                  placeholder="Name *"
                  data-testid="activation-ap-name"
                  className="h-9 text-sm"
                />
                <Input
                  type="email"
                  value={ap.email || ""}
                  onChange={(e) => setAp("email", e.target.value)}
                  placeholder="Email *"
                  data-testid="activation-ap-email"
                  className="h-9 text-sm"
                />
                <Input
                  value={ap.title || ""}
                  onChange={(e) => setAp("title", e.target.value)}
                  placeholder="Title"
                  data-testid="activation-ap-title"
                  className="h-9 text-sm"
                />
                <Input
                  value={ap.phone || ""}
                  onChange={(e) => setAp("phone", e.target.value)}
                  placeholder="Phone"
                  data-testid="activation-ap-phone"
                  className="h-9 text-sm"
                />
              </div>
            </div>
          )}

          <div className="space-y-2">
            {CHECKLIST_ITEMS.map(item => (
              <div 
                key={item.key}
                onClick={() => setChecklist({ ...checklist, [item.key]: !checklist[item.key] })}
                className={`flex items-center gap-3 p-2.5 sm:p-3 rounded-lg border cursor-pointer transition-colors ${
                  checklist[item.key] ? "bg-green-50 border-green-200" : "bg-secondary/30 border-border hover:bg-secondary/50"
                }`}
              >
                <Checkbox
                  checked={checklist[item.key]}
                  onCheckedChange={v => setChecklist({ ...checklist, [item.key]: v })}
                  className="pointer-events-none"
                />
                <div className="flex-1 min-w-0">
                  <p className="font-medium text-sm truncate">{item.label}</p>
                  <p className="text-xs text-muted-foreground hidden sm:block">{item.desc}</p>
                </div>
                {checklist[item.key] && <CheckCircle2 className="w-4 h-4 sm:w-5 sm:h-5 text-green-600 flex-shrink-0" />}
              </div>
            ))}
          </div>
        </CardContent>
      </Card>
    </div>
  );
}
